import React, { createContext, useContext, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../auth/auth.context";
import { getUserProgress } from "../services/userProgressService";
import cacheService from "../services/cacheService";

// Create context
const UserProgressContext = createContext();

export const UserProgressProvider = ({ children }) => {
    const { currentUser } = useAuth();
    const queryClient = useQueryClient();

    // Use TanStack Query to fetch user progress
    const {
        data: userProgress = {},
        isLoading: loading,
        error
    } = useQuery({
        queryKey: ["userProgress", currentUser?.uid],
        queryFn: () => getUserProgress(currentUser.uid),
        enabled: !!currentUser, // Only run if user is logged in
        staleTime: 15 * 60 * 1000, // 15 minutes cache
        gcTime: 30 * 60 * 1000, // 30 minutes in memory (formerly cacheTime)
    });

    /**
     * Get progress for a specific module
     */
    const getProgress = useCallback((moduleId) => {
        return userProgress[moduleId] || null;
    }, [userProgress]);

    /**
     * Check if a module is completed
     */
    const isModuleCompleted = useCallback((moduleId) => {
        const progress = userProgress[moduleId] || null;
        return progress?.completed === true;
    }, [userProgress]);

    const getProgressModuleVersion = useCallback((moduleId) => {
        const progress = userProgress[moduleId] || null;
        return progress?.version ?? 1;
    }, [userProgress]);

    /**
     * Get the current step ID for a module
     */
    const getCurrentStepId = useCallback((moduleId) => {
        const progress = userProgress[moduleId] || null;
        return progress?.currentStepId || progress?.stepId || null;
    }, [userProgress]);

    /**
     * Check if a module is in progress
     */
    const isModuleInProgress = useCallback((moduleId) => {
        const progress = userProgress[moduleId] || null;
        if (!progress) return false;

        const hasStarted = progress.currentStepId || progress.stepId;
        const notCompleted = !progress.completed;
        const stepIdNotCompleted = hasStarted && hasStarted !== "completed";

        return notCompleted && stepIdNotCompleted;
    }, [userProgress]);

    /**
     * Get the quiz score for a module
     */
    const getQuizScore = useCallback((moduleId) => {
        const progress = userProgress[moduleId] || null;
        return progress?.quizScore || null;
    }, [userProgress]);

    // Force refresh user progress data
    const refreshProgress = useCallback(() => {
        if (currentUser) {
            // Invalidate query to trigger refetch
            queryClient.invalidateQueries({ queryKey: ["userProgress", currentUser.uid] });

            // Clear legacy cache if needed
            const userProgressCacheKey = cacheService.createKey(
                "user_progress",
                currentUser.uid
            );
            cacheService.delete(userProgressCacheKey);
            cacheService.deleteByPrefix(`module_progress_${currentUser.uid}`);
        }
    }, [queryClient, currentUser]);

    const value = {
        loading,
        error,
        userProgress: userProgress || {},
        getProgress,
        isModuleCompleted,
        getCurrentStepId,
        isModuleInProgress,
        getQuizScore,
        getProgressModuleVersion,
        refreshProgress,
    };

    return (
        <UserProgressContext.Provider value={value}>
            {children}
        </UserProgressContext.Provider>
    );
};

export default UserProgressContext;
